import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SyncResult } from '../models/permit-sync.model';
import {
    AddInternalNotePayload,
    AddSubtasksPayload,
    AdminCreatePermitPayload,
    AdminPermitDetail,
    AdminPermitListItem,
    AdminSendMessagePayload,
    AdminUpdatePermitPayload,
    AssignSubtaskPayload,
    AttachFilePayload,
    CreateLinkPayload,
    EditInternalNotePayload,
    InternalFilePayload,
    PermitCreatedResponse,
    UpdateCategoryAndTypePayload,
    UpdateDepartmentPayload,
    UpdateFeePayload,
    UpdateLinkPayload,
    UpdateStatusPayload,
    UpdateSubtaskPayload,
    UpdateTagsPayload,
} from '../models/permit.model';
import { AdminPermitFilters, PermitPaginatedResult } from '../models/permit-filters.model';
import { PermitV2Service } from './permits.abstract.service';

@Injectable()
export class PermitV2ImplService extends PermitV2Service {
    private readonly baseUrl = `${environment.API_GATEWAY_URL}/permit/admin`;

    constructor(private http: HttpClient) {
        super();
    }

    getPermits(filters: AdminPermitFilters = {}): Observable<PermitPaginatedResult<AdminPermitListItem>> {
        const params = this.buildFilterParams(filters);
        return this.http.get<PermitPaginatedResult<AdminPermitListItem>>(`${this.baseUrl}/permit`, { params });
    }

    getPermitById(id: string): Observable<AdminPermitDetail> {
        return this.http.get<AdminPermitDetail>(`${this.baseUrl}/permit/${id}`);
    }

    createPermit(payload: AdminCreatePermitPayload): Observable<PermitCreatedResponse> {
        return this.http.post<PermitCreatedResponse>(`${this.baseUrl}/permit`, payload);
    }

    updatePermit(id: string, payload: AdminUpdatePermitPayload): Observable<AdminPermitDetail> {
        return this.http.patch<AdminPermitDetail>(`${this.baseUrl}/permit/${id}`, payload);
    }

    deletePermit(id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/permit/${id}`);
    }

    updateStatus(id: string, payload: UpdateStatusPayload): Observable<any> {
        return this.http.patch(`${this.baseUrl}/permit/${id}/status`, payload);
    }

    updateDepartment(id: string, payload: UpdateDepartmentPayload): Observable<any> {
        return this.http.patch(`${this.baseUrl}/permit/${id}/department`, payload);
    }

    updateTags(id: string, payload: UpdateTagsPayload): Observable<any> {
        return this.http.patch(`${this.baseUrl}/permit/${id}/tags`, payload);
    }

    updateCategoryAndType(id: string, payload: UpdateCategoryAndTypePayload): Observable<AdminPermitDetail> {
        return this.http.patch<AdminPermitDetail>(`${this.baseUrl}/permit/${id}/category-and-type`, payload);
    }

    updateFee(id: string, payload: UpdateFeePayload): Observable<any> {
        return this.http.patch(`${this.baseUrl}/permit/${id}/fee`, payload);
    }

    // Messages
    sendMessage(id: string, payload: AdminSendMessagePayload): Observable<any> {
        return this.http.post(`${this.baseUrl}/permit/${id}/message`, payload);
    }

    markMessagesAsRead(id: string): Observable<any> {
        return this.http.get(`${this.baseUrl}/permit/${id}/message/read`);
    }

    // Files
    uploadTempFile(file: File): Observable<string[]> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<string[]>(`${environment.API_GATEWAY_URL}/requests/fileupload/temp`, formData).pipe(
            map((response: any) => response as string[]),
            catchError((error) => throwError(error)),
        );
    }

    attachFiles(id: string, payload: AttachFilePayload): Observable<any> {
        return this.http.post(`${this.baseUrl}/permit/${id}/files`, payload);
    }

    deleteFile(id: string, index: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/permit/${id}/files/${index}`);
    }

    attachInternalFiles(id: string, payload: InternalFilePayload): Observable<any> {
        return this.http.post(`${this.baseUrl}/permit/${id}/internal-files`, payload);
    }

    deleteInternalFile(id: string, index: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/permit/${id}/internal-files/${index}`);
    }

    // Internal notes
    addInternalNote(id: string, payload: AddInternalNotePayload): Observable<any> {
        return this.http.post(`${this.baseUrl}/permit/${id}/internal-notes`, payload);
    }

    editInternalNote(id: string, noteId: string, payload: EditInternalNotePayload): Observable<any> {
        return this.http.patch(`${this.baseUrl}/permit/${id}/internal-notes/${noteId}`, payload);
    }

    // Subtasks
    addSubtasks(id: string, payload: AddSubtasksPayload): Observable<any> {
        return this.http.patch(`${this.baseUrl}/permit/${id}/subtask`, payload);
    }

    updateSubtask(id: string, subtaskId: string, payload: UpdateSubtaskPayload): Observable<any> {
        return this.http.patch(`${this.baseUrl}/permit/${id}/subtask/${subtaskId}`, payload);
    }

    assignSubtask(id: string, subtaskId: string, payload: AssignSubtaskPayload): Observable<any> {
        return this.http.patch(`${this.baseUrl}/permit/${id}/subtask/${subtaskId}/assign`, payload);
    }

    deleteSubtask(id: string, subtaskId: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/permit/${id}/subtask/${subtaskId}`);
    }

    // Links
    createLink(id: string, payload: CreateLinkPayload): Observable<any> {
        return this.http.post(`${this.baseUrl}/permit/${id}/link`, payload);
    }

    updateLink(id: string, payload: UpdateLinkPayload): Observable<any> {
        return this.http.patch(`${this.baseUrl}/permit/${id}/link`, payload);
    }

    deleteLink(id: string, linkedPermitId: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/permit/${id}/link/${linkedPermitId}`);
    }

    // ─── Connector sync (PSD) ───────────────────────────────────────────────
    syncConnectorCategories(): Observable<SyncResult> {
        return this.http.post<SyncResult>(`${this.baseUrl}/permit/connector/sync/categories`, {});
    }

    syncConnectorPermits(): Observable<SyncResult> {
        return this.http.post<SyncResult>(`${this.baseUrl}/permit/connector/sync/permits`, {});
    }

    // ─── Private helpers ────────────────────────────────────────────────────
    private buildFilterParams(filters: AdminPermitFilters): HttpParams {
        let params = new HttpParams();
        if (filters.status) params = params.set('status', filters.status);
        if (filters.category) params = params.set('category', filters.category);
        if (filters.permitType) params = params.set('permitType', filters.permitType);
        if (filters.department) params = params.set('department', filters.department);
        if (filters.assignedTo) params = params.set('assignedTo', filters.assignedTo);
        if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
        if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
        if (filters.search) params = params.set('search', filters.search);
        if (filters.syncStatus) params = params.set('syncStatus', filters.syncStatus);
        if (filters.tags?.length) {
            for (const tag of filters.tags) {
                params = params.append('tags', tag);
            }
        }
        if (filters.page) params = params.set('page', filters.page.toString());
        if (filters.limit) params = params.set('limit', filters.limit.toString());
        if (filters.sort) params = params.set('sort', filters.sort);
        if (filters.sortOrder) params = params.set('sortOrder', filters.sortOrder);
        return params;
    }
}
